import React, { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import JsonInput, { JsonInputRef } from './components/JsonInput';
import PathDisplay from './components/PathDisplay';
import SearchBar from './components/SearchBar';
import Toast from './components/Toast';
import SEOHead from './components/SEOHead';
import LanguageSwitcher from './components/LanguageSwitcher';
import ThemeToggle from './components/ThemeToggle';
import { extractJsonPaths, flattenPaths, PathNode } from './utils/jsonPathUtils';
import { useDebounce } from './utils/searchUtils';

const FIREWORK_PARTICLES = [
  { x: '18%', y: '22%', tx: '-78px', ty: '-62px', color: 'rgb(var(--color-primary-container))', delay: '0ms' },
  { x: '18%', y: '22%', tx: '-28px', ty: '-88px', color: 'rgb(var(--color-secondary))', delay: '28ms' },
  { x: '18%', y: '22%', tx: '46px', ty: '-72px', color: 'rgb(var(--color-tertiary-container))', delay: '56ms' },
  { x: '18%', y: '22%', tx: '82px', ty: '-18px', color: 'rgb(var(--color-primary-container))', delay: '84ms' },
  { x: '18%', y: '22%', tx: '54px', ty: '58px', color: 'rgb(var(--color-secondary-container))', delay: '112ms' },
  { x: '18%', y: '22%', tx: '-58px', ty: '44px', color: 'rgb(var(--color-primary))', delay: '140ms' },
  { x: '58%', y: '34%', tx: '-84px', ty: '-28px', color: 'rgb(var(--color-secondary))', delay: '10ms' },
  { x: '58%', y: '34%', tx: '-34px', ty: '-92px', color: 'rgb(var(--color-primary-container))', delay: '38ms' },
  { x: '58%', y: '34%', tx: '48px', ty: '-84px', color: 'rgb(var(--color-tertiary-container))', delay: '66ms' },
  { x: '58%', y: '34%', tx: '94px', ty: '-6px', color: 'rgb(var(--color-secondary-container))', delay: '94ms' },
  { x: '58%', y: '34%', tx: '36px', ty: '86px', color: 'rgb(var(--color-primary-container))', delay: '122ms' },
  { x: '58%', y: '34%', tx: '-68px', ty: '66px', color: 'rgb(var(--color-primary))', delay: '150ms' },
  { x: '78%', y: '18%', tx: '-68px', ty: '-54px', color: 'rgb(var(--color-tertiary-container))', delay: '20ms' },
  { x: '78%', y: '18%', tx: '12px', ty: '-82px', color: 'rgb(var(--color-primary-container))', delay: '48ms' },
  { x: '78%', y: '18%', tx: '76px', ty: '-36px', color: 'rgb(var(--color-secondary))', delay: '76ms' },
  { x: '78%', y: '18%', tx: '70px', ty: '44px', color: 'rgb(var(--color-secondary-container))', delay: '104ms' },
  { x: '78%', y: '18%', tx: '2px', ty: '76px', color: 'rgb(var(--color-primary-container))', delay: '132ms' },
  { x: '78%', y: '18%', tx: '-74px', ty: '30px', color: 'rgb(var(--color-primary))', delay: '160ms' }
];

function getPathStats(paths: PathNode[]) {
  const flattenedPaths = flattenPaths(paths);
  let maxDepth = 0;
  let arrays = 0;
  let objects = 0;

  function visit(node: PathNode, depth: number) {
    maxDepth = Math.max(maxDepth, depth);
    if (node.type === 'array') arrays += 1;
    if (node.type === 'object') objects += 1;
    node.children?.forEach((child) => visit(child, depth + 1));
  }

  paths.forEach((path) => visit(path, 1));

  return {
    total: flattenedPaths.length,
    maxDepth,
    arrays,
    objects
  };
}

function formatSelectedPath(path: string | null) {
  if (!path || path === '') return '$';
  if (path.startsWith('[')) return `$${path}`;
  return `$.${path}`;
}

function PathSelectionFireworks({ burstId }: { burstId: number }) {
  return (
    <div key={burstId} className="path-fireworks" aria-hidden="true">
      <span className="path-fireworks__beam path-fireworks__beam--one" />
      <span className="path-fireworks__beam path-fireworks__beam--two" />
      <span className="path-fireworks__ring path-fireworks__ring--one" />
      <span className="path-fireworks__ring path-fireworks__ring--two" />
      {FIREWORK_PARTICLES.map((particle, index) => (
        <span
          key={`${burstId}-${index}`}
          className="path-fireworks__particle"
          style={{
            '--x': particle.x,
            '--y': particle.y,
            '--tx': particle.tx,
            '--ty': particle.ty,
            '--particle-color': particle.color,
            '--particle-delay': particle.delay
          } as React.CSSProperties}
        />
      ))}
    </div>
  );
}

function App() {
  const { t } = useTranslation();
  const [paths, setPaths] = useState<PathNode[]>([]);
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [toastVisible, setToastVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isRegexSearch, setIsRegexSearch] = useState(false);
  const [enableJSON5, setEnableJSON5] = useState(false);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [pathBurstId, setPathBurstId] = useState(0);
  const jsonInputRef = useRef<JsonInputRef>(null);
  
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const pathStats = getPathStats(paths);

  const handleJsonChange = useCallback((json: any) => {
    if (json) {
      const extractedPaths = extractJsonPaths(json);
      setPaths(extractedPaths);
      setJsonError(null);
    } else {
      setPaths([]);
    }
    setSelectedPath(null);
  }, []);

  const handleError = useCallback((error: string | null) => {
    if (error) {
      setPaths([]);
      setSelectedPath(null);
    }
    setJsonError(error);
  }, []);

  const handlePathSelect = useCallback(async (path: string) => {
    setSelectedPath(path);
    setPathBurstId((currentBurstId) => currentBurstId + 1);
    
    const fullPath = formatSelectedPath(path);
    
    try {
      await navigator.clipboard.writeText(fullPath);
      setToastVisible(true);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  }, []);

  const handleToastClose = useCallback(() => {
    setToastVisible(false);
  }, []);

  const handleSearch = useCallback((query: string, isRegex: boolean) => {
    setSearchQuery(query);
    setIsRegexSearch(isRegex);
  }, []);

  const handleReset = useCallback(() => {
    setPaths([]);
    setSelectedPath(null);
    setToastVisible(false);
    setSearchQuery('');
    setIsRegexSearch(false);
    setJsonError(null);
    jsonInputRef.current?.reset();
  }, []);

  return (
    <div className="app-shell min-h-screen flex flex-col bg-transparent font-body text-on-background">
      <SEOHead />
      
      <header className="sticky top-0 z-50 border-b border-white/10 bg-surface-container-lowest/80 px-4 py-3 shadow-[0_16px_50px_rgba(0,0,0,0.35)] backdrop-blur-2xl sm:px-6">
        <div className="mx-auto flex max-w-[1440px] flex-wrap items-center justify-between gap-3">
          <button
                onClick={handleReset}
            className="group flex min-w-0 items-center gap-3 text-left"
            aria-label="Reset JSON Path Finder"
              >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-primary-container/30 bg-primary-container/10 shadow-[0_0_24px_rgba(0,242,255,0.16)]">
              <img alt="" className="brand-logo h-4 w-8 object-contain" src="/leanofy_logo_white.svg" />
            </span>
            <span className="min-w-0">
              <img alt="Leanofy" className="brand-logo h-5 w-32 object-contain object-left sm:w-40" src="/leanofy_logo_white.svg" />
              <span className="mt-1 block truncate font-code text-[10px] uppercase tracking-[0.34em] text-primary-container/75">{t('header.title')}</span>
            </span>
          </button>
            
          <nav className="order-3 flex w-full items-center gap-2 overflow-x-auto pt-1 font-display text-sm font-semibold text-on-surface-variant sm:order-none sm:w-auto sm:pt-0" aria-label="Main navigation">
            <a className="rounded-full border border-primary-container/40 bg-primary-container/10 px-4 py-2 text-primary-container shadow-[0_0_18px_rgba(0,242,255,0.14)]" href="#explorer">Explorer</a>
            <a className="rounded-full px-4 py-2 text-outline transition hover:bg-white/5 hover:text-primary" href="#documentation">{t('nav.documentation')}</a>
            <a className="rounded-full px-4 py-2 text-outline transition hover:bg-white/5 hover:text-primary" href="#schema">{t('nav.schema')}</a>
          </nav>

          <div className="flex flex-wrap items-center gap-2">
                <LanguageSwitcher />
                <ThemeToggle />
              
            <label className="flex min-h-[40px] cursor-pointer items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-2 font-code text-[11px] uppercase tracking-[0.14em] text-on-surface-variant transition hover:bg-primary-container/10 hover:text-primary-container">
                  <input
                    type="checkbox"
                    checked={enableJSON5}
                    onChange={(e) => setEnableJSON5(e.target.checked)}
                className="h-4 w-4 rounded border-outline-variant bg-surface-container text-primary-container focus:ring-primary-container"
                  />
              <span className="whitespace-nowrap">{t('header.json5Support')}</span>
                </label>
              </div>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-[1440px] flex-1 flex-col gap-5 px-4 py-5 sm:px-6 lg:px-8">
        <section className="glass-panel rounded-2xl px-5 py-5 sm:px-6 lg:px-8">
          <div className="relative z-10 grid gap-5 lg:grid-cols-[1.1fr_0.9fr] lg:items-end">
            <div>
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <span className="rounded-full border border-primary-container/35 bg-primary-container/10 px-3 py-1 font-code text-[11px] uppercase tracking-[0.22em] text-primary-container">Live extractor</span>
                <span className="rounded-full border border-secondary/30 bg-secondary/10 px-3 py-1 font-code text-[11px] uppercase tracking-[0.22em] text-secondary">{enableJSON5 ? 'JSON5 enabled' : 'JSON ready'}</span>
                {jsonError && <span className="rounded-full border border-error/35 bg-error-container/20 px-3 py-1 font-code text-[11px] uppercase tracking-[0.22em] text-error">Invalid input</span>}
              </div>
              <h1 className="font-display text-3xl font-bold leading-tight tracking-[-0.04em] text-primary sm:text-4xl lg:text-5xl">{t('header.subtitle')}</h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-on-surface-variant sm:text-base">
                {t('header.description')}
              </p>
            </div>
            <div className="grid grid-cols-3 gap-2 font-code text-[11px] uppercase tracking-[0.16em] text-outline sm:gap-3">
              <div className="rounded-xl border border-white/10 bg-white/[0.035] p-3">
                <span className="block text-2xl font-bold tracking-tight text-primary-container">{pathStats.total}</span>
                paths
              </div>
              <div className="rounded-xl border border-white/10 bg-white/[0.035] p-3">
                <span className="block text-2xl font-bold tracking-tight text-secondary">{pathStats.maxDepth || '--'}</span>
                depth
              </div>
              <div className="rounded-xl border border-white/10 bg-white/[0.035] p-3">
                <span className="block text-2xl font-bold tracking-tight text-tertiary-container">{jsonError ? 'error' : paths.length > 0 ? 'valid' : 'idle'}</span>
                parser
              </div>
            </div>
          </div>
        </section>

        <section id="explorer" className={`grid flex-1 grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] ${paths.length > 0 ? 'min-h-[640px]' : 'min-h-[calc(100vh-360px)]'}`}>
          <div className="glass-panel glow-border relative z-10 flex min-h-[520px] flex-col rounded-2xl p-3 sm:min-h-[620px] sm:p-4 lg:min-h-0">
            <JsonInput 
              ref={jsonInputRef} 
              onJsonChange={handleJsonChange} 
              onError={handleError}
              selectedPath={selectedPath}
              enableJSON5={enableJSON5}
            />
          </div>
          
          <div className="glass-panel glow-border relative z-10 flex min-h-[520px] flex-col rounded-2xl p-3 sm:min-h-[620px] sm:p-4 lg:min-h-0">
            {pathBurstId > 0 && <PathSelectionFireworks burstId={pathBurstId} />}
            <div className="mb-4 flex flex-col gap-4 border-b border-white/10 pb-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="font-code text-[11px] uppercase tracking-[0.28em] text-secondary/75">Explorer</p>
                <h2 className="font-display text-2xl font-semibold tracking-[-0.03em] text-on-surface">{t('jsonPaths.title')}</h2>
                <p className="mt-1 text-sm text-on-surface-variant">{t('jsonPaths.subtitle')}</p>
              </div>
              <div className="grid grid-cols-3 gap-2 font-code text-[10px] uppercase tracking-[0.12em] text-outline sm:min-w-[220px]">
                <span className="rounded-lg border border-primary-container/25 bg-primary-container/10 px-2 py-2 text-primary-container">Nodes {pathStats.total}</span>
                <span className="rounded-lg border border-secondary/25 bg-secondary/10 px-2 py-2 text-secondary">Obj {pathStats.objects}</span>
                <span className="rounded-lg border border-tertiary-container/25 bg-tertiary-container/10 px-2 py-2 text-tertiary-container">Arr {pathStats.arrays}</span>
              </div>
            </div>

            {selectedPath && (
              <div className="mb-4 rounded-xl border border-primary-container/20 bg-primary-container/5 p-3 sm:p-4">
                <h3 className="mb-2 font-display text-sm font-semibold text-on-surface">{t('selectedPath.title')}</h3>
                <div className="code-surface rounded-lg border border-primary-container/20 p-3 font-code text-sm text-primary-container break-all">
                  {formatSelectedPath(selectedPath)}
                </div>
                <p className="mt-2 text-xs text-on-surface-variant">
                  {t('selectedPath.copied')}
                </p>
              </div>
            )}
            
            {paths.length > 0 && (
              <SearchBar onSearch={handleSearch} />
            )}
            
            <PathDisplay 
              paths={paths} 
              onPathSelect={handlePathSelect}
              selectedPath={selectedPath}
              searchQuery={debouncedSearchQuery}
              isRegexSearch={isRegexSearch}
            />
          </div>
        </section>

        <section id="documentation" className="glass-panel rounded-2xl px-5 py-5 sm:px-6 lg:px-8" aria-labelledby="documentation-title">
          <div className="relative z-10 grid gap-5 lg:grid-cols-[0.9fr_1.1fr]">
            <div>
              <p className="font-code text-[11px] uppercase tracking-[0.28em] text-secondary/75">{t('documentation.eyebrow')}</p>
              <h2 id="documentation-title" className="mt-2 font-display text-2xl font-semibold tracking-[-0.03em] text-on-surface sm:text-3xl">
                {t('documentation.title')}
              </h2>
              <p className="mt-3 text-sm leading-6 text-on-surface-variant sm:text-base">
                {t('documentation.description')}
              </p>
            </div>
            <div className="grid gap-3 md:grid-cols-3">
              <article className="rounded-xl border border-white/10 bg-white/[0.035] p-4">
                <span className="font-code text-xs uppercase tracking-[0.2em] text-primary-container">01</span>
                <h3 className="mt-2 font-display text-base font-semibold text-on-surface">{t('documentation.steps.paste.title')}</h3>
                <p className="mt-2 text-sm leading-6 text-on-surface-variant">{t('documentation.steps.paste.text')}</p>
              </article>
              <article className="rounded-xl border border-white/10 bg-white/[0.035] p-4">
                <span className="font-code text-xs uppercase tracking-[0.2em] text-secondary">02</span>
                <h3 className="mt-2 font-display text-base font-semibold text-on-surface">{t('documentation.steps.browse.title')}</h3>
                <p className="mt-2 text-sm leading-6 text-on-surface-variant">{t('documentation.steps.browse.text')}</p>
              </article>
              <article className="rounded-xl border border-white/10 bg-white/[0.035] p-4">
                <span className="font-code text-xs uppercase tracking-[0.2em] text-tertiary-container">03</span>
                <h3 className="mt-2 font-display text-base font-semibold text-on-surface">{t('documentation.steps.use.title')}</h3>
                <p className="mt-2 text-sm leading-6 text-on-surface-variant">{t('documentation.steps.use.text')}</p>
              </article>
            </div>
          </div>
        </section>

        <section id="schema" className="glass-panel rounded-2xl px-5 py-5 sm:px-6 lg:px-8" aria-labelledby="schema-title">
          <div className="relative z-10 grid gap-5 lg:grid-cols-[1fr_1fr]">
            <div>
              <p className="font-code text-[11px] uppercase tracking-[0.28em] text-tertiary-container/80">{t('schema.eyebrow')}</p>
              <h2 id="schema-title" className="mt-2 font-display text-2xl font-semibold tracking-[-0.03em] text-on-surface sm:text-3xl">
                {t('schema.title')}
              </h2>
              <p className="mt-3 text-sm leading-6 text-on-surface-variant sm:text-base">
                {t('schema.description')}
              </p>
            </div>
            <div className="code-surface rounded-xl border border-primary-container/20 p-4 font-code text-xs leading-6 text-on-surface-variant sm:text-sm">
              <p><span className="text-secondary">object</span> - {t('schema.items.object')}</p>
              <p><span className="text-secondary">array[n]</span> - {t('schema.items.array')}</p>
              <p><span className="text-secondary">string | number | boolean | null</span> - {t('schema.items.value')}</p>
              <p><span className="text-primary-container">$.store.book[0].title</span> - {t('schema.items.example')}</p>
            </div>
          </div>
        </section>

        <section className="glass-panel rounded-2xl px-5 py-5 sm:px-6 lg:px-8" aria-labelledby="json-path-finder-guide">
          <div className="relative z-10 grid gap-5 lg:grid-cols-[0.9fr_1.1fr]">
            <div>
              <p className="font-code text-[11px] uppercase tracking-[0.28em] text-primary-container/75">{t('seoContent.eyebrow')}</p>
              <h2 id="json-path-finder-guide" className="mt-2 font-display text-2xl font-semibold tracking-[-0.03em] text-on-surface sm:text-3xl">
                {t('seoContent.title')}
              </h2>
              <p className="mt-3 text-sm leading-6 text-on-surface-variant sm:text-base">
                {t('seoContent.description')}
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <article className="rounded-xl border border-white/10 bg-white/[0.035] p-4">
                <h3 className="font-display text-base font-semibold text-primary-container">{t('seoContent.cards.extract.title')}</h3>
                <p className="mt-2 text-sm leading-6 text-on-surface-variant">{t('seoContent.cards.extract.text')}</p>
              </article>
              <article className="rounded-xl border border-white/10 bg-white/[0.035] p-4">
                <h3 className="font-display text-base font-semibold text-secondary">{t('seoContent.cards.validate.title')}</h3>
                <p className="mt-2 text-sm leading-6 text-on-surface-variant">{t('seoContent.cards.validate.text')}</p>
              </article>
              <article className="rounded-xl border border-white/10 bg-white/[0.035] p-4">
                <h3 className="font-display text-base font-semibold text-tertiary-container">{t('seoContent.cards.use.title')}</h3>
                <p className="mt-2 text-sm leading-6 text-on-surface-variant">{t('seoContent.cards.use.text')}</p>
              </article>
            </div>
          </div>
        </section>
      </main>

      <Toast 
        message={t('toast.pathCopied')}
        isVisible={toastVisible}
        onClose={handleToastClose}
      />

      <footer className="mt-auto border-t border-white/10 bg-surface-container-lowest/80 px-4 py-5 backdrop-blur-xl sm:px-6">
        <div className="mx-auto flex max-w-[1440px] flex-col gap-4 font-display text-xs uppercase tracking-[0.22em] text-outline sm:flex-row sm:items-center sm:justify-between">
            <div className="text-center sm:text-left">
            © 2026 <span className="font-semibold text-on-surface">LEANOFY</span>. {t('footer.copyright')}
            </div>
            <div className="flex flex-wrap justify-center gap-x-5 gap-y-2">
              <a 
                href="https://leanofy.de/datenschutz" 
                target="_blank" 
                rel="noopener noreferrer"
                className="transition-colors duration-200 hover:text-primary-container"
              >
                {t('footer.privacy')}
              </a>
              <a 
                href="https://leanofy.de/impressum" 
                target="_blank" 
                rel="noopener noreferrer"
                className="transition-colors duration-200 hover:text-primary-container"
              >
                {t('footer.imprint')}
              </a>
            </div>
          </div>
      </footer>
    </div>
  );
}

export default App;
